import type { Neighborhood } from "../../types/neighborhood";
import { fetchServiceAreaPlacesClient } from "./fetchServiceAreaPlacesClient";
import { promiseWithTimeout } from "./promiseWithTimeout";

export type FetchServiceAreaPlacesParams = {
  placesUrl: string;
  csrfToken: string;
  googleMapsApiKey?: string;
  query: string;
  center: { lat: number; lng: number } | null;
  country?: string;
  excludeNames: string[];
  signal?: AbortSignal;
};

const SERVER_SEARCH_TIMEOUT_MS = 10_000;

function fetchAborted(signal?: AbortSignal): boolean {
  return signal?.aborted ?? false;
}

async function fetchServiceAreaPlacesFromServer(
  params: FetchServiceAreaPlacesParams,
): Promise<Neighborhood[]> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    SERVER_SEARCH_TIMEOUT_MS,
  );

  const onAbort = () => controller.abort();
  params.signal?.addEventListener("abort", onAbort);

  try {
    const paramsQuery = new URLSearchParams({ q: params.query });
    if (params.center) {
      paramsQuery.set("lat", String(params.center.lat));
      paramsQuery.set("lng", String(params.center.lng));
    }
    if (params.country) {
      paramsQuery.set("country", params.country);
    }
    params.excludeNames.forEach((name) =>
      paramsQuery.append("exclude[]", name),
    );

    const response = await fetch(
      `${params.placesUrl}?${paramsQuery.toString()}`,
      {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": params.csrfToken,
        },
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      throw new Error("Failed to search areas");
    }

    const data = (await response.json()) as { places?: Neighborhood[] };
    return data.places ?? [];
  } finally {
    window.clearTimeout(timeoutId);
    params.signal?.removeEventListener("abort", onAbort);
  }
}

export async function fetchServiceAreaPlaces(
  params: FetchServiceAreaPlacesParams,
): Promise<Neighborhood[]> {
  if (fetchAborted(params.signal)) {
    return [];
  }

  if (params.googleMapsApiKey && typeof window !== "undefined") {
    try {
      const places = await fetchServiceAreaPlacesClient({
        ...params,
        googleMapsApiKey: params.googleMapsApiKey,
      });
      if (!fetchAborted(params.signal)) {
        return places;
      }
      return [];
    } catch (error) {
      if (fetchAborted(params.signal)) {
        return [];
      }
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "Client-side Places search failed; falling back to server",
          error,
        );
      }
    }
  }

  if (!params.placesUrl) {
    return [];
  }

  try {
    return await promiseWithTimeout(
      fetchServiceAreaPlacesFromServer(params),
      SERVER_SEARCH_TIMEOUT_MS,
      "Server area search timed out",
    );
  } catch {
    return [];
  }
}
